import { Box, Typography } from "@mui/material";
import { useId } from "react";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { MotifBadge } from "@/shared/components";
import { motifDescription, tacticalDescription } from "../review-copy";

import {
  boardEvidenceMotifAnnotation,
  boardMotifAnnotation,
  type BoardMotifAnnotation,
} from "@/shared/components/motif-annotation";

export {
  boardEvidenceMotifAnnotation,
  boardMotifAnnotation,
  boardMotifColor,
  BOARD_TACTICAL_ANNOTATIONS,
  type BoardMotifAnnotation,
} from "@/shared/components/motif-annotation";

interface BoardMotifBadgeProps {
  move: AnalyzedMoveResult | null;
  annotation?: { destination: string; motifs: string[]; presentation?: BoardMotifAnnotation } | null;
  orientation: "white" | "black";
}

export const BOARD_MOTIF_Z_INDEX = 24;

export const boardMoveDestination = (move: AnalyzedMoveResult): string | null => {
  const side = move.fenBefore.split(" ")[1];
  if (move.san.startsWith("O-O-O")) return side === "b" ? "c8" : "c1";
  if (move.san.startsWith("O-O")) return side === "b" ? "g8" : "g1";
  return /^[a-h][1-8]$/.test(move.playedTo) ? move.playedTo : null;
};

export const BoardMotifBadge = ({ move, annotation: liveAnnotation, orientation }: BoardMotifBadgeProps) => {
  const disclosureId = useId();
  const square = liveAnnotation?.destination ?? (move ? boardMoveDestination(move) : null);
  const annotation = liveAnnotation?.presentation ?? boardMotifAnnotation(liveAnnotation?.motifs ?? move?.motifs ?? []);
  if (!square || !annotation) return null;
  const sourceMotif = (liveAnnotation?.motifs ?? move?.motifs ?? []).find(
    (motif) => boardEvidenceMotifAnnotation([motif])?.title === annotation.title,
  );
  const description =
    annotation.evidenceKind === "tactical"
      ? tacticalDescription(sourceMotif ?? "")
      : motifDescription(sourceMotif ?? "", move ?? undefined);
  const line = move?.bestMoveSan ? [move.bestMoveSan, ...(move.bestContinuation ?? [])] : [];
  const evidenceCopy = `${description}${line.length ? ` Verified line: ${line.join(" ")}.` : ""}`;

  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]);
  const column = orientation === "white" ? file : 7 - file;
  const row = orientation === "white" ? 8 - rank : rank - 1;

  const disclosureSide = column <= 3 ? "right" : "left";
  const disclosureTop = Math.max(18, Math.min(82, (row + 0.5) * 12.5));
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: BOARD_MOTIF_Z_INDEX,
        pointerEvents: "none",
        "& .motif-trigger:hover + .motif-disclosure, & .motif-trigger:focus-visible + .motif-disclosure": {
          opacity: 1,
          visibility: "visible",
          transform: "translateY(-50%)",
        },
      }}
    >
      <Box
        component="span"
        role="img"
        tabIndex={0}
        data-motif-interactive
        className="motif-trigger"
        aria-label={annotation.title}
        aria-describedby={disclosureId}
        sx={{
          position: "absolute",
          pointerEvents: "auto",
          left: `${(column + 1) * 12.5}%`,
          top: `${row * 12.5}%`,
          width: "clamp(20px, 4.8%, 28px)",
          aspectRatio: "1",
          transform: "translate(-62%,-38%)",
          transition: "none !important",
          animation: "none !important",
          opacity: 1,
          "&::after": {
            content: '""',
            position: "absolute",
            top: 2,
            right: 2,
            width: 3,
            height: 3,
            bgcolor: annotation.color,
          },
        }}
      >
        <MotifBadge kind={annotation.kind} label={annotation.label} color={annotation.color} />
      </Box>
      <Box
        id={disclosureId}
        role="tooltip"
        data-motif-disclosure
        className="motif-disclosure"
        sx={{
          position: "absolute",
          zIndex: 40,
          top: `${disclosureTop}%`,
          width: "min(238px, calc(50% - 16px))",
          maxHeight: "calc(100% - 16px)",
          overflowY: "auto",
          opacity: 0,
          visibility: "hidden",
          transform: "translateY(calc(-50% + 6px))",
          transition: "opacity 160ms ease, transform 220ms cubic-bezier(.22,1,.36,1)",
          ...(disclosureSide === "right" ? { left: "calc(50% + 8px)" } : { right: "calc(50% + 8px)" }),
          p: 1.5,
          bgcolor: "#0a0a0a",
          color: "#f0ece2",
          border: "2px solid",
          borderColor: annotation.color,
          boxShadow: "6px 6px 0 rgba(0,0,0,.55)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <Typography sx={{ color: annotation.color, fontSize: ".6rem", fontWeight: 950, letterSpacing: ".12em" }}>
          {liveAnnotation?.presentation ? "BOARD EVIDENCE" : "MOVE CLASSIFICATION"}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 950, letterSpacing: "-.035em" }}>
          {annotation.title}
        </Typography>
        <Typography sx={{ mt: 0.75, color: "text.secondary", fontSize: ".72rem", lineHeight: 1.45 }}>
          {evidenceCopy}
        </Typography>
      </Box>
    </Box>
  );
};
